import React, { useEffect, useState, useRef, ReactNode, useContext, createContext } from 'react';
import styled from 'styled-components';

const VisibleSectionContext = createContext<{
  visibleSection: string | null;
  setVisibleSection: (id: string | null) => void;
}>({
  visibleSection: null,
  setVisibleSection: () => {},
});

export const VisibleSectionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [visibleSection, setVisibleSection] = useState<string | null>(null);

  return (
    <VisibleSectionContext.Provider value={{ visibleSection, setVisibleSection }}>
      {children}
    </VisibleSectionContext.Provider>
  );
};

interface SectionProps {
  id: string;
  title: string;
  children?: ReactNode;
}

const Section: React.FC<SectionProps> = ({ id, title, children }) => {
  const { visibleSection, setVisibleSection } = useContext(VisibleSectionContext);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSection(id);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [id, setVisibleSection]);

  const isVisible = visibleSection === id;

  return (
    <StyledSection ref={sectionRef} id={id}>
      <Title isVisible={isVisible}>{title}</Title>
      <Content>{children}</Content>
    </StyledSection>
  );
};

const StyledSection = styled.section`
  padding: 80px 20px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f9f9f9, #eaeaea);
  border-bottom: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 35px 15px;
  }
`;

const Title = styled.h2<{ isVisible: boolean }>`
  font-size: 2rem;
  font-family: 'Cutive Mono', monospace;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) => (props.isVisible ? 'translateY(0)' : 'translateY(20px)')};
  transition: opacity 0.5s ease, transform 0.5s ease;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Content = styled.div`
  font-size: 1.2rem;
  color: #555;
  text-align: center;
  width: 100%;
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export default Section;